"use client"

import type { ReactNode } from "react"

import { useLocale, useTranslations } from "next-intl"

import { CartLineRow } from "@/components/marketplace/cart/cart-line-row"
import { OrderSummary } from "@/components/marketplace/cart/order-summary"
import { TabEmptyState } from "@/components/marketplace/cart/tab-empty-state"
import { ProductCarousel } from "@/components/marketplace/product/product-carousel"
import type { CartLine, Product } from "@/components/marketplace/types"
import { Button, Cents, Heading, Row, Stack, Text } from "@/components/ui"
import { LocalizedClientLink } from "@/components/ui/localized-client-link"
import { RevealScope } from "@/components/ui/reveal-scope"
import { resolveSupportedLocale } from "@/i18n/locales"
import { addMoney, DEFAULT_CURRENCY, formatMoneyWithCode, money, multiplyMoney, subtractMoney } from "@/lib/money"

interface CartViewProps {
  lines: CartLine[]
  showHeader?: boolean
  suggestions?: Product[]
  outOfStock?: boolean
  signupAction?: ReactNode
  onCheckout?: () => void
  onRemove?: (id: string) => void
}

export const CartView = ({ lines, suggestions, outOfStock = false, showHeader = true, signupAction, onCheckout, onRemove }: CartViewProps) => {
  const t = useTranslations("cart")

  const locale = resolveSupportedLocale(useLocale())
  const zero = money("0", lines[0]?.price.currency ?? DEFAULT_CURRENCY)
  const count = lines.reduce((sum, line) => sum + line.qty, 0)
  const total = lines.reduce((sum, line) => addMoney(sum, multiplyMoney(line.price, line.qty)), zero)
  const savings = lines.reduce((sum, line) => addMoney(sum, line.was ? multiplyMoney(subtractMoney(line.was, line.price), line.qty) : zero), zero)
  const empty = lines.length === 0

  return (
    <div className="max-w-page motion-safe:animate-rise-in @container mx-auto w-full">
      <RevealScope />
      {showHeader && (
        <div className="px-gut py-500 @3xl:py-700">
          <Heading as="h1" variant="lg" family="display" tone="default" className="@3xl:text-display-sm font-extrabold">
            {t("title")}
            {count > 0 && <span className="text-fg-muted font-bold"> · {count}</span>}
          </Heading>
        </div>
      )}

      {empty ? (
        <div className="px-gut">
          <TabEmptyState
            title={
              <>
                {t("emptyTitle")} <span className="text-fg-brand">{t("emptyTitleAccent")}</span>
              </>
            }
            body={t("emptyBodyShort")}
            action={
              <Stack gap="300" align="center">
                <Button asChild>
                  <LocalizedClientLink href="/">{t("exploreStoreShort")}</LocalizedClientLink>
                </Button>
                {signupAction}
              </Stack>
            }
          />
        </div>
      ) : (
        <div className="px-gut @3xl:grid @3xl:grid-cols-12 @3xl:items-start @3xl:gap-600">
          <div className="@3xl:col-span-7">
            <Stack gap="250">
              {lines.map((line) => (
                <CartLineRow key={line.id} line={line} onRemove={onRemove ? () => onRemove(line.id) : undefined} />
              ))}
            </Stack>
          </div>

          <div className="@3xl:top-sticky-page mt-400 @2xl:max-w-105 @3xl:sticky @3xl:col-span-5 @3xl:mt-0 @3xl:max-w-none">
            <OrderSummary
              total={total}
              count={count}
              savings={savings}
              installment={lines.length === 1 ? lines[0]?.payments : undefined}
              installments={lines.length === 1 ? lines[0]?.installments : undefined}
              lineCount={lines.length}
              freeShippingLines={lines.filter((line) => line.freeship).length}
              outOfStock={outOfStock}
              onCheckout={onCheckout}
            />
          </div>
        </div>
      )}

      {suggestions && suggestions.length > 0 && (
        <div className="mt-800 pb-1200">
          {empty ? (
            <ProductCarousel headerSize="utility" title={t("emptyRailTitle")} accent="Nelo" products={suggestions} />
          ) : (
            <ProductCarousel headerSize="utility" title={t("alsoLike")} products={suggestions} />
          )}
        </div>
      )}

      {!empty && (
        <div className="bg-surface border-line-subtle z-sticky px-gut pb-safe-offset-300 group-data-bottom-nav/shell:bottom-dock-clear sticky bottom-0 mt-600 border-t py-300 group-data-bottom-nav/shell:pb-300 @3xl:hidden">
          <Row justify="between" gap="300" align="center">
            <Stack gap="0">
              <Text as="span" variant="caption" family="text" tone="muted">
                {t("totalWithCount", { count })}
              </Text>
              <Text as="span" variant="body" family="display" tone="brand" numeric className="font-semibold">
                <Cents>{formatMoneyWithCode(total, locale)}</Cents>
              </Text>
            </Stack>
            <Button className="flex-1" disabled={outOfStock} onClick={onCheckout}>
              {outOfStock ? t("outOfStock") : t("continueToPayment")}
            </Button>
          </Row>
        </div>
      )}
    </div>
  )
}
